/**
 * Release maths of CadinuLockV2, mirrored from its verified source (`_withdrawableTokens`, `_normalUnlock`, `_vestingUnlock`).
 * Everything is bigint and mirrors the contract's integer rounding, so what the page says is releasable is what `unlock` would pay.
 *
 *   plain lock      tgeBps = 0: after `tgeDate` the owner can withdraw the whole amount once.
 *   vesting lock    tgeBps > 0 (a vesting lock needs tgeBps, cycleBps, cycle all above zero):
 *                   released(now) = floor((now - tgeDate) / cycle) * floor(amount * cycleBps / 10000) + floor(amount * tgeBps / 10000),
 *                   capped at the amount; the owner withdraws released(now) - unlockedAmount.
 */
export type V2Terms = {
  amount: bigint
  unlockedAmount: bigint
  /** unlock date of a plain lock, TGE date of a vesting lock */
  tgeDate: number
  tgeBps: number
  cycle: number
  cycleBps: number
}

export const BPS = 10000n
const MAX_ROWS = 4000

export const isVesting = (t: Pick<V2Terms, 'tgeBps'>) => t.tgeBps > 0

const tgeRelease = (t: V2Terms) => (t.amount * BigInt(t.tgeBps)) / BPS
const cycleRelease = (t: V2Terms) => (t.amount * BigInt(t.cycleBps)) / BPS

/** What the contract counts as released at `now` for a vesting lock, before the cap by the amount. */
const releasedUncapped = (t: V2Terms, now: number): bigint => {
  if (now < t.tgeDate || t.cycle <= 0) return 0n
  return BigInt(Math.floor((now - t.tgeDate) / t.cycle)) * cycleRelease(t) + tgeRelease(t)
}

/** Raw units the owner could withdraw right now (what `withdrawableTokens` returns for a vesting lock, the whole remainder for a plain one). */
export function withdrawableAt(t: V2Terms, now: number): bigint {
  if (t.amount === 0n || t.unlockedAmount >= t.amount) return 0n
  if (now < t.tgeDate) return 0n
  if (!isVesting(t)) return t.unlockedAmount === 0n ? t.amount : 0n
  if (t.cycle <= 0) return 0n
  const total = releasedUncapped(t, now)
  return total > t.amount ? t.amount - t.unlockedAmount : total > t.unlockedAmount ? total - t.unlockedAmount : 0n
}

/** Raw units still held that cannot be withdrawn yet. */
export function lockedAt(t: V2Terms, now: number): bigint {
  if (t.unlockedAmount >= t.amount) return 0n
  const held = t.amount - t.unlockedAmount
  const out = held - withdrawableAt(t, now)
  return out > 0n ? out : 0n
}

/** The cycle index (0 = the TGE release) at which the contract's released total first reaches the amount. null when it never will. */
export function finalCycle(t: V2Terms): number | null {
  if (!isVesting(t)) return 0
  if (t.cycle <= 0) return null
  const start = tgeRelease(t)
  if (start >= t.amount) return 0
  const step = cycleRelease(t)
  if (step === 0n) return null
  const k = (t.amount - start + step - 1n) / step
  return k > 1_000_000n ? null : Number(k)
}

/** The last release date of the lock (the unlock date of a plain lock). null when a vesting lock would never finish. */
export function endDate(t: V2Terms): number | null {
  const k = finalCycle(t)
  return k === null ? null : t.tgeDate + k * (isVesting(t) ? t.cycle : 0)
}

export type ReleaseRow = { index: number; at: number; releaseRaw: bigint; cumulativeRaw: bigint; cumulativeBps: number }

/** Every release of the lock in order, as the contract would make it (the plain lock has one row). Capped so a tiny cycle cannot hang a page. */
export function releaseSchedule(t: V2Terms, limit = MAX_ROWS): ReleaseRow[] {
  const rows: ReleaseRow[] = []
  const bpsOf = (x: bigint) => (t.amount === 0n ? 0 : Number((x * BPS) / t.amount))
  if (!isVesting(t)) {
    rows.push({ index: 0, at: t.tgeDate, releaseRaw: t.amount, cumulativeRaw: t.amount, cumulativeBps: 10000 })
    return rows
  }
  const last = finalCycle(t)
  if (last === null) return rows
  let prev = 0n
  for (let k = 0; k <= last && rows.length < limit; k++) {
    const total = BigInt(k) * cycleRelease(t) + tgeRelease(t)
    const cum = total >= t.amount ? t.amount : total
    rows.push({ index: k, at: t.tgeDate + k * t.cycle, releaseRaw: cum - prev, cumulativeRaw: cum, cumulativeBps: bpsOf(cum) })
    prev = cum
  }
  return rows
}

/** The next date at which more becomes withdrawable, or null when everything already is (or the lock is finished). */
export function nextRelease(t: V2Terms, now: number): number | null {
  if (t.unlockedAmount >= t.amount) return null
  if (now < t.tgeDate) return t.tgeDate
  if (!isVesting(t)) return null
  if (releasedUncapped(t, now) >= t.amount) return null
  const k = Math.floor((now - t.tgeDate) / t.cycle) + 1
  return t.tgeDate + k * t.cycle
}
